import classnames from "classnames";
import React from "react";
import OpeningHours from "../opening-hours/OpeningHours";
import LiveChatButton from "../../../live-chat-button/LiveChatButton";
import { Badge } from "@JohnLewisPartnership/jl-design-system/dist/components/badge/Badge";
import { TickCircleFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleFilled";
import ContactLiveChatUnavailable from "../contact-live-chat-unavailable/ContactLiveChatUnavailable";
import { useFeatureFlag } from "@/context/FeatureFlagContext";

import styles from "./contact-chat.module.scss";

const liveChatDescriptions = [
  "Our fast, text-based chat service",
  "Our chatbot can connect you with a Partner",
  "Send photos and save the chat for your records",
];

function ContactLiveChat({
  className,
  config,
  location,
  displayOpeningHours = true,
  displayDescriptionParagraph = true,
  displayHeading = true,
}) {
  const { channel, open, displayTimes, percentage } = config || {};
  const getIfEnabled = () => Math.random() < percentage / 100;

  const showNewProminentLiveChat = useFeatureFlag("show_new_prominent_live_chat");

  const unavailableChatOption = () => {
    return showNewProminentLiveChat ? (
      <ContactLiveChatUnavailable config={config} showGreyBackground={false} />
    ) : (
      <div>
        <br />
        <p>
          <b>Currently unavailable</b>
        </p>
        <p>You can still get in touch by calling our Customer Service team.</p>
        {channel === "TechnicalSupport" && <OpeningHours openingHours={displayTimes} />}
      </div>
    );
  };

  const outOfHoursOption = showNewProminentLiveChat ? (
    <ContactLiveChatUnavailable config={config} showGreyBackground={false} />
  ) : (
    <div>
      <p>Our team is not available for live chat at this time.</p>
      <br />

      {channel === "TechnicalSupport" && (
        <>
          <p>
            <b>Opening times</b>
          </p>
          <OpeningHours openingHours={displayTimes} />
        </>
      )}
    </div>
  );

  const openChatOption = () => {
    const showLiveChat = showChatIcon => (
      <>
        <div className={styles["contact-chat__button"]}>
          <LiveChatButton channel={channel} buttonLocation={location} showChatIcon={showChatIcon} />
        </div>
        {displayOpeningHours && <OpeningHours openingHours={displayTimes} />}
      </>
    );

    return showNewProminentLiveChat ? (
      <>
        <div>
          <div className={styles.liveChatHeader}>
            <h4 className={styles["contact-chat__new-title"]}>Live chat</h4>
            <Badge badgeType="--success" className="custom-class-name" label="Recommended" />
          </div>
          {liveChatDescriptions.map(desc => (
            <div key={desc} className={styles.liveChatDescription}>
              <div>
                <TickCircleFilled />
              </div>
              <p>{desc}</p>
            </div>
          ))}
        </div>
        {showLiveChat(true)}
      </>
    ) : (
      <div>
        {displayDescriptionParagraph && (
          <p>Contact our team via our website. You can save the chat transcript for reference.</p>
        )}
        {showLiveChat(false)}
      </div>
    );
  };

  const getChatDOM = () => {
    if (!getIfEnabled()) {
      return unavailableChatOption();
    }
    if (!open) {
      return outOfHoursOption;
    }
    return openChatOption();
  };

  return (
    <div className={classnames(className, styles[className], styles["contact-chat"])} data-test="container-chat">
      {displayHeading && (
        <>{!showNewProminentLiveChat && <h4 className={styles["contact-chat__title"]}>Live chat</h4>}</>
      )}
      {getChatDOM()}
    </div>
  );
}

export default ContactLiveChat;
